import { Location } from '../../_shared/interfaces/location';
import {
  IHandpieceDefaultsProfiles,
  HandpiecePresetKey,
  IHandpiecePreset,
  HandpieceProfileKey,
} from '../interfaces/handpieceProfiles';
import { Equipment } from '../interfaces/equipment';
import { instance } from './instance';
import { getEquipments } from './manage-equipment.service';
import { isHandpiece } from '../../utils';

export const setHandpieceProfileOverride = async (
  loc: Location,
  profileKey: HandpieceProfileKey,
  name: string
) => {
  try {
    await instance.post(`/hp-profiles/handpiece-profile-location-override`, {
      orgId: loc.orgId,
      locationId: loc.id,
      profileKey,
      name,
    });

    return true;
  } catch (err) {
    return false;
  }
};

export const setHandpiecePresetOverride = async (
  loc: Location,
  newPreset: IHandpiecePreset,
  profileKey: HandpieceProfileKey,
  presetKey: HandpiecePresetKey
) => {
  try {
    await instance.post(`/hp-profiles/handpiece-preset-location-overrides`, {
      orgId: loc.orgId,
      locationId: loc.id,
      preset: {
        ...newPreset,
        profileKey,
        presetKey,
      },
    });

    return true;
  } catch (err) {
    return false;
  }
};

// AER-1720: no `firmware` / `version` arguments any more. Handpiece defaults are
// universal, so there is nothing left for them to select. They also actively hid the
// bug this ticket fixes: every caller left them defaulted, so reads always asked for
// firmware 1 -- the only defaults row that existed -- and rendered happily while every
// write, which used the device's real firmware, failed its lookup.
export const getHandpieceProfilesForLocation = async (
  loc: Location
): Promise<IHandpieceDefaultsProfiles | undefined> => {
  try {
    const response = await instance.get(
      `/hp-profiles/handpiece-presets-for-location?orgId=${loc.orgId}&locId=${loc.id}`
    );
    // todo: this response is wonk -- why is it sometimes one way or the other.
    // investigate this on backend
    if (response && response.data && response.data.Items) {
      return response.data.Items[0] as IHandpieceDefaultsProfiles;
    }
    if (response && response.data) {
      return response.data as IHandpieceDefaultsProfiles;
    }
    return undefined;
  } catch (err) {
    return undefined;
  }
};

/**
 * AER-1719: everything thrown on the way to a handpiece has to arrive back at the
 * caller as a readable reason. `err.message` on its own was not enough -- the
 * server's `error` field is thrown below as a bare string, and a string has no
 * `.message`, so the failure came back as `{ error: undefined }`, which every
 * caller reads as "nothing went wrong".
 */
const failureReason = (err: unknown): string => {
  if (typeof err === 'string' && err.length > 0) {
    return err;
  }
  const message = err ? (err as { message?: string }).message : undefined;
  return message || 'the handpiece could not be updated';
};

export const resetHandpiecePresets = async (
  equipment: Equipment,
  orgId: string,
  locationId: string
): Promise<{ error?: string; synced: string[]; failed: string[] }> => {
  // AER-1719: prep and endo are two halves of ONE device configuration, so the two
  // responses have to be reconciled rather than concatenated. A device the prep call
  // reported as synced and the endo call reported as failed is not configured, and
  // has to come back as failed.
  const synced: string[] = [];
  const failed: string[] = [];

  const collect = (result: { synced?: string[]; failed?: string[] }) => {
    (result.synced || []).forEach(id => {
      if (synced.indexOf(id) < 0) {
        synced.push(id);
      }
    });
    (result.failed || []).forEach(id => {
      if (failed.indexOf(id) < 0) {
        failed.push(id);
      }
    });
  };

  const outcome = (error?: string) => ({
    error,
    synced: synced.filter(id => failed.indexOf(id) < 0),
    failed,
  });

  try {
    const prepResult = await instance.post(
      `/hp-profiles/handpiece-reset-prep-profiles`,
      {
        handpiece: equipment,
        orgId,
        locationId,
      }
    );
    if (!prepResult || prepResult.status !== 200) {
      throw new Error('request failed');
    }
    if (!prepResult.data) {
      throw new Error('the server returned no sync result');
    }
    if (prepResult.data.error) {
      throw prepResult.data.error;
    }

    collect(prepResult.data);

    const endoResult = await instance.post(
      `/hp-profiles/handpiece-reset-endo-profiles`,
      {
        handpiece: equipment,
        orgId,
        locationId,
      }
    );
    if (!endoResult || endoResult.status !== 200) {
      throw new Error('request failed');
    }
    if (!endoResult.data) {
      throw new Error('the server returned no sync result');
    }
    if (endoResult.data.error) {
      throw endoResult.data.error;
    }

    collect(endoResult.data);

    return outcome();
  } catch (err) {
    // AER-1719: keep whatever the first half already reported. Returning empty arrays
    // here erased the devices prep had ALREADY failed on, so a caller that reports
    // `failed[]` had nothing to report.
    return outcome(failureReason(err));
  }
};

/**
 * AER-1721: the wording the add-to-location screens use to report a sync, in one
 * place. All three of them (Edit Location, Edit Org, Manage Location) have to say the
 * same thing about the same result, and none of them may promise a recovery that does
 * not exist -- there is no scheduled retry today, and the recovery being built
 * (AER-1722 / AER-1723) fires when the device next publishes rather than on a timer.
 *
 * `synced` and `failed` hold DEVICE ids since AER-1719, so their lengths are handpiece
 * counts -- which is what the numbers below claim to be. They were command counts
 * before, and a single unreachable handpiece would have reported as 60.
 */
export const handpieceSyncMessage = (result: {
  error?: string;
  synced: string[];
  failed: string[];
}): { ok: boolean; text: string } => {
  const failedCount = result.failed.length;
  const syncedCount = result.synced.length;

  // AER-1722: the failure is remembered now -- the server marks the device `needsSync`
  // before it starts pushing and clears it only once both halves come back clean, so the
  // unit shows as out of sync until it is fixed. Said only when the server actually
  // produced the breakdown: with an `error` the call may never have reached it, and there
  // is no point telling the user about a record that was never written. Note what this
  // deliberately does NOT say -- nothing re-attempts these on its own yet. Recovery is
  // AER-1723, and it will be triggered by the device reconnecting, not by a schedule.
  const tracked = result.error ? '' : ' They are marked out of sync.';

  if (failedCount > 0 && syncedCount > 0) {
    const total = failedCount + syncedCount;
    return {
      ok: false,
      text: `Profile and Preset data could not be sent to ${failedCount} of ${total} handpiece unit(s).${tracked}`,
    };
  }

  // No ids on either side and no reason given means nothing was pushed at all, which is
  // not something to congratulate the user on.
  if (result.error || failedCount > 0 || syncedCount === 0) {
    return {
      ok: false,
      text: `Profile and Preset data could not be sent to handpiece unit(s).${
        failedCount > 0 ? tracked : ''
      }`,
    };
  }

  return {
    ok: true,
    text: `Profile and Preset data successfully sent to handpiece unit(s).`,
  };
};

/**
 * AER-1722: what the add-to-location screens say *before* the push, now that they no
 * longer wait for it. Kept next to `handpieceSyncMessage` for the same reason that one
 * exists: every screen has to say the same thing, and no message may promise a recovery
 * that has not been built.
 */
export const handpieceSyncStartedMessage = (count: number): string =>
  `Profile and Preset data is being sent to ${count} handpiece unit(s) in the background. Units that do not confirm it are marked out of sync.`;

/**
 * AER-1722: start a sync without holding the caller open for it.
 *
 * AER-1721 made the three add-to-location paths await the push so that a failure could be
 * reported at all, which is right but costs the user `handpieces x ~60` sequential device
 * commands with the form spinning -- minutes, for a save whose actual subject was the
 * equipment assignment. That trade only existed because a failure that nobody was waiting
 * for was a failure nobody would ever hear about again. It is no longer: the server marks
 * each device out of sync before it starts pushing and clears it only on a confirmed
 * success, so an outcome the browser never sees is still recorded on the equipment record.
 *
 * `onComplete` still fires if the app is open when the push finishes, so the user loses
 * the wait and not the report. It is not a guarantee -- a closed tab or a navigation that
 * tears down the request means only the server-side record is left, which is the point.
 *
 * Nothing here can reject: `syncHandpieces` resolves with its outcome for every failure
 * mode, so this cannot leave an unhandled rejection behind.
 */
export const syncHandpiecesInBackground = (
  handpieces: Equipment[],
  orgId: string,
  locationId: string,
  onComplete?: (outcome: { ok: boolean; text: string }) => void
): void => {
  if (handpieces.length === 0 || !orgId || !locationId) {
    return;
  }

  syncHandpieces(handpieces, orgId, locationId).then(result => {
    if (onComplete) {
      onComplete(handpieceSyncMessage(result));
    }
  });
};

/**
 * AER-1721: the per-handpiece push loop, split out of `syncHandpiecesForLocation` so
 * that the add-to-location paths can hand it the handpieces they have just assigned.
 * Those paths cannot go through the location-scoped helper below: it re-reads the
 * location's equipment, which both re-pushes to every handpiece already there and races
 * the writes that have only just been made. What they need to share is this -- one
 * sequential pass and one honest outcome per device -- not the lookup.
 */
export const syncHandpieces = async (
  handpieces: Equipment[],
  orgId: string,
  locationId: string
): Promise<{ error?: string; synced: string[]; failed: string[] }> => {
  const synced: string[] = [];
  const failed: string[] = [];
  let error: string | undefined;

  for (const handpiece of handpieces) {
    const result = await resetHandpiecePresets(handpiece, orgId, locationId);

    const failedHere = result.failed.slice();
    if (result.error) {
      // AER-1719: keep the FIRST reason. Later handpieces usually fail for the same
      // cause, and the first is the one the user's action actually ran into.
      error = error || result.error;
      // AER-1719: a handpiece whose call errored outright did not receive its
      // configuration either, so it belongs in `failed` with the rest. Its id used to
      // vanish -- only the message survived -- which is why a partial failure could not
      // be told apart from a total one by the call sites that count `failed[]`.
      if (handpiece.deviceId && failedHere.indexOf(handpiece.deviceId) < 0) {
        failedHere.push(handpiece.deviceId);
      }
    }

    result.synced.forEach(id => {
      if (synced.indexOf(id) < 0) {
        synced.push(id);
      }
    });
    failedHere.forEach(id => {
      if (failed.indexOf(id) < 0) {
        failed.push(id);
      }
    });
  }

  // AER-1719: as above -- failed anywhere outranks synced anywhere else.
  return { error, synced: synced.filter(id => failed.indexOf(id) < 0), failed };
};

/**
 * AER-1704 / AER-1705: re-sync every handpiece AT A LOCATION after that
 * location's profile/preset configuration has changed.
 *
 * The scope is deliberately the LOCATION and not the organization. The payload
 * the server pushes to a handpiece is built purely from the location
 * (`createFlattenedPresetList(locationId, ...)`), the overrides are stored on
 * the location record (`handpieceDefaultsProfileOverrides` /
 * `handpieceDefaultsOverrides`), and every endpoint involved is named for the
 * location. `orgId` is carried through for context/auth but never resolves a
 * setting, so syncing org-wide pushes one location's clinical settings onto
 * handpieces that were never meant to receive them.
 *
 * Callers must go through this helper rather than writing their own loop, so
 * the scope decision is made in exactly one place. AER-1721: the exception is a
 * caller that already knows exactly which handpieces it just assigned -- it
 * calls `syncHandpieces` directly rather than re-reading the location.
 *
 * The handpieces are synced one at a time on purpose: each call fans out into
 * up to 30 `set_cfg` device commands server-side, so issuing them in parallel
 * would put a burst on the device-command path for no user-visible gain.
 */
export const syncHandpiecesForLocation = async (
  loc: Location
): Promise<{ error?: string; synced: string[]; failed: string[] }> => {
  const synced: string[] = [];
  const failed: string[] = [];
  const orgId = loc && loc.orgId;

  if (!loc || !orgId) {
    return { error: 'location is missing an organization', synced, failed };
  }

  // AER-1719: a location whose handpieces cannot even be resolved has not been synced.
  // This used to reject straight out of the helper -- past manage-profile's catch-less
  // try/finally -- and the user was left with a spinner that stopped and no message.
  let handpieces: Equipment[];
  try {
    handpieces = (await getEquipments({
      locationId: { eq: loc.id },
    })).filter(e => isHandpiece(e));
  } catch (err) {
    return { error: failureReason(err), synced, failed };
  }

  return syncHandpieces(handpieces, orgId, loc.id);
};
